import type { StreamingBlock } from "@storytell/streaming";
import { BlockRenderer } from "@storytell/streaming";
import { Show } from "solid-js";
import { BlockHeaderPill } from "./internal/BlockHeaderPill";
import { getBlockIconComponent } from "./internal/blockIcons";

interface InterstitialDataRendererProps {
  block: StreamingBlock;
}

const formatCredits = (credits: number): string => {
  if (credits === 0) return "0 credits";
  return `${credits.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} credits`;
};

/**
 * Renders data-oriented interstitial blocks with a pill header showing title and processing state.
 * Shows egress credits instead of content.
 */
export const InterstitialDataRenderer = (props: InterstitialDataRendererProps) => {
  const title = () => props.block.title || props.block.activity || "Data";
  const egressCredits = () => props.block.metrics?.egressCredits || 0;

  return (
    <Show when={!props.block.isComplete || egressCredits() > 0}>
      <div data-renderer="interstitial-data-renderer">
        <div class="flex items-center gap-2">
          <Show when={!props.block.isComplete}>
            <BlockHeaderPill
              title={title()}
              icon={getBlockIconComponent(props.block, BlockRenderer.INTERSTITIAL_DATA, { variant: "purple" })}
              isProcessing={!props.block.isComplete}
              onClick={() => {
                // no-op: the pill is a status indicator here, not a control
              }}
              variant="purple"
            />
          </Show>
        </div>

        <Show when={egressCredits() > 0}>
          <div
            class="ml-6 text-xs text-gray-500 dark:text-gray-400"
            classList={{
              "mt-3": !props.block.isComplete,
            }}
          >
            {formatCredits(egressCredits())}
          </div>
        </Show>
      </div>
    </Show>
  );
};
